import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { HelpSiteDataDTO } from '../../../common/dto/help-site.dto';
import { KnowledgeBaseArticleDTO } from '../../../common/dto/knowledge-base.dto';
import { DEFAULT_LANGUAGE, Language } from '../../../common/dto/languages.dto';

export interface AppStateModel {
  isInitialized: boolean;

  siteData?: HelpSiteDataDTO;

  isNavOpen: boolean;

  language: Language;

  isHomePageOpen: boolean;

  openedArticle: KnowledgeBaseArticleDTO | undefined;
}

export class SetSiteDataAction {
  static readonly type = '[AppState] SetSiteDataAction';

  constructor(public siteData: HelpSiteDataDTO | undefined) {
  }
}

export class SetOpenedArticleAction {
  static readonly type = '[AppState] SetOpenedArticleAction';

  constructor(public articleId: KnowledgeBaseArticleDTO | undefined) {
  }
}

export class SetLanguageAction {
  static readonly type = '[AppState] SetLanguageAction';

  constructor(public language: Language) {
  }
}

export class ChangeNavOpenedAction {
  static readonly type = '[AppState] OpenNavAction';

  constructor(
    public isNavOpen: boolean,
  ) {
  }
}

export class ChangeIsHomePageOpenAction {
  static readonly type = '[AppState] ChangeIsHomePageOpenAction';

  constructor(
    public isHomePageOpen: boolean,
  ) {
  }
}

export const AppStateName = 'helpAppState';

export function selectAppState(store: Store): Observable<AppStateModel> {
  return store.select<AppStateModel>((state: any) => state[AppStateName]);
}

export function selectAppStateSnapshot(store: Store): AppStateModel {
  return store.selectSnapshot<AppStateModel>((state: any) => state[AppStateName]);
}

@State<AppStateModel>({
  name: AppStateName,
  defaults: {
    isInitialized: false,
    siteData: undefined,
    isNavOpen: false,
    isHomePageOpen: false,
    openedArticle: undefined,
    language: DEFAULT_LANGUAGE,
  },
})
@Injectable()
export class AppState {
  @Selector([AppState])
  public static get(state: AppStateModel): AppStateModel {
    return state;
  }

  @Selector([AppState])
  public static siteData(state: AppStateModel): HelpSiteDataDTO | undefined {
    return state.siteData;
  }

  @Selector([AppState])
  public static articleOpened(state: AppStateModel): KnowledgeBaseArticleDTO | undefined {
    return state.openedArticle;
  }

  @Selector([AppState])
  public static language(state: AppStateModel): Language {
    return state.language;
  }

  @Action(SetSiteDataAction)
  public setSiteDataAction(ctx: StateContext<AppStateModel>, action: SetSiteDataAction) {
    ctx.patchState({
      isInitialized: true,
      siteData: action.siteData,
      language: action.siteData?.language ?? DEFAULT_LANGUAGE,
    });
  }

  @Action(SetLanguageAction)
  public setLanguageAction(ctx: StateContext<AppStateModel>, action: SetLanguageAction) {
    ctx.patchState({
      language: action.language,
    });
  }

  @Action(ChangeNavOpenedAction)
  public changeNavOpenedAction(ctx: StateContext<AppStateModel>, action: ChangeNavOpenedAction) {
    ctx.patchState({
      isNavOpen: action.isNavOpen,
    });
  }

  @Action(ChangeIsHomePageOpenAction)
  public changeIsHomePageOpenAction(ctx: StateContext<AppStateModel>, action: ChangeIsHomePageOpenAction) {
    ctx.patchState({
      isHomePageOpen: action.isHomePageOpen,
    });
  }

  @Action(SetOpenedArticleAction)
  public setOpenedArticleAction(ctx: StateContext<AppStateModel>, action: SetOpenedArticleAction) {
    ctx.patchState({
      openedArticle: action.articleId,
    });
  }
}
